import { useCallback, useReducer } from 'react';

/**
 * The split button (piece 3, Button): when text[] or actions[] has more than
 * one entry the Button shows a chevron and two click zones. Choosing an entry
 * from the open panel re-arms the main zone and fires nothing; a panel whose
 * entries fire on selection is a menu, not a split button.
 */
export interface SplitButtonState {
  armed: number;
  open: boolean;
}

export type SplitButtonEvent = { type: 'toggle' } | { type: 'close' } | { type: 'select'; index: number };

export const initialSplitButton: SplitButtonState = { armed: 0, open: false };

export function splitButtonReducer(state: SplitButtonState, event: SplitButtonEvent): SplitButtonState {
  switch (event.type) {
    case 'toggle':
      return { ...state, open: !state.open };
    case 'close':
      return { ...state, open: false };
    case 'select':
      return { armed: event.index, open: false };
  }
}

export function isSplit(textCount: number, actionCount: number): boolean {
  return textCount > 1 || actionCount > 1;
}

export interface SplitButtonOptions {
  text: readonly string[];
  actions: readonly (() => void)[];
  disabled?: boolean;
}

export function useSplitButton({ text, actions, disabled = false }: SplitButtonOptions) {
  const [state, dispatch] = useReducer(splitButtonReducer, initialSplitButton);
  const split = isSplit(text.length, actions.length);
  const armedLabel = text[state.armed] ?? text[0] ?? '';
  const toggle = useCallback(() => {
    if (!disabled) dispatch({ type: 'toggle' });
  }, [disabled]);
  const close = useCallback(() => dispatch({ type: 'close' }), []);
  const select = useCallback((index: number) => dispatch({ type: 'select', index }), []);
  /** The main zone runs the armed action; a disabled button takes no click. */
  const fire = useCallback(() => {
    if (disabled) return;
    const action = actions[state.armed] ?? actions[0];
    action?.();
  }, [actions, disabled, state.armed]);
  return { split, open: state.open, armed: state.armed, armedLabel, toggle, close, select, fire };
}
